import { supabase } from "./client";
import type { LinkEmployeeResponse, RequestInviteResponse, SelfRegisterResponse } from "../types/entry";
import type { CompanyEmergencyContact, CompanyResolution } from "../types/company";
import type { EmployeeRow } from "../types/profile";
import type { CompanyRole } from "../types/navigation";
import type {
  EmployeeModuleFeedbackQuestion,
  EmployeeModuleFeedbackRow,
} from "../types/modules";
import type {
  AvAssessmentQuestion,
  KontrolScoreFeedbackAdminRow,
  KontrolScoreFeedbackAnswers,
  KontrolScoreFeedbackQuestion,
  KontrolScoreFeedbackRow,
} from "../types/scores";
import { questionMatchesAssessmentOccasion } from "../pages/utils/assessmentOccasion";

export const SCORE_TYPES = ["PBG", "Ext_PRS", "E_Score"] as const;

export const MODULE_WITH_TASKS_SELECT = `
  id,
  name_en,
  name_de,
  description_en,
  description_de,
  video_url,
  estimated_minutes,
  kontrol_score_pct,
  order_index,
  is_active,
  created_at,
  module_task_categories (
    id,
    module_id,
    order_index,
    title_en,
    title_de,
    description_en,
    description_de,
    created_at,
    updated_at,
    module_tasks (
      id,
      category_id,
      task_en,
      task_de,
      created_at,
      updated_at
    )
  )
`;

// Reads the current Supabase Auth session so the app can restore navigation and call authenticated edge functions.
export function getCurrentSession() {
  return supabase.auth.getSession();
}

// Reads the current Supabase Auth user for screens that need to map auth_user_id to an employee row.
export function getCurrentUser() {
  return supabase.auth.getUser();
}

// Subscribes to auth changes so navigation can refresh the visible role when login state changes.
export function onAuthStateChange(callback: () => void) {
  return supabase.auth.onAuthStateChange(() => {
    callback();
  });
}

// Signs an existing user in with email/password credentials from the entry screen.
export function signInWithPassword(email: string, password: string) {
  return supabase.auth.signInWithPassword({ email, password });
}

// Creates a new Supabase Auth account before linking or self-registering the employee row.
export function signUpWithPassword(email: string, password: string) {
  return supabase.auth.signUp({ email, password });
}

// Ends the current Supabase Auth session during logout or account deletion cleanup.
export function signOut() {
  return supabase.auth.signOut();
}

// Loads an employee's role by auth user id for routing and navigation authorization.
export function fetchEmployeeRoleByAuthUserId(authUserId: string) {
  return supabase
    .from("employees")
    .select("company_role")
    .eq("auth_user_id", authUserId)
    .maybeSingle();
}

// Loads an employee's role by internal employee id after account linking.
export function fetchEmployeeRoleById(employeeId: string) {
  return supabase
    .from("employees")
    .select("company_role")
    .eq("id", employeeId)
    .maybeSingle();
}

// Maps a Supabase Auth user id to the internal employee id.
export function fetchEmployeeIdByAuthUserId(authUserId: string) {
  return supabase
    .from("employees")
    .select("id")
    .eq("auth_user_id", authUserId)
    .maybeSingle();
}

// Persists which event should drive score calculations for this assessment run.
export function upsertEmployeeAssessmentOccasion(employeeId: string, eventId: string) {
  return supabase
    .from("employee_assessment_occasions")
    .upsert(
      { employee_id: employeeId, event_id: eventId, updated_at: new Date().toISOString() },
      { onConflict: "employee_id" }
    );
}

// Loads employee id and company id for the personal KONTROL score page.
export function fetchEmployeeScoreIdentityByAuthUserId(authUserId: string) {
  return supabase
    .from("employees")
    .select("id,company_id")
    .eq("auth_user_id", authUserId)
    .maybeSingle();
}

// Loads the employee id and age needed to seed the profile assessment age range.
export function fetchProfileAssessmentEmployeeByAuthUserId(authUserId: string) {
  return supabase
    .from("employees")
    .select("id, age")
    .eq("auth_user_id", authUserId)
    .maybeSingle();
}

// Loads the readiness-check employee details shown and optionally edited before assessments begin.
export function fetchReadinessEmployeeByAuthUserId(authUserId: string) {
  return supabase
    .from("employees")
    .select("id,first_name,last_name,age,position,location,gender,company_role,company_email,department,company_id")
    .eq("auth_user_id", authUserId)
    .maybeSingle();
}

// Saves editable readiness-check admin fields on the employee profile.
export function updateReadinessEmployeeInfo(employeeId: string, values: Record<string, unknown>) {
  return supabase.from("employees").update(values).eq("id", employeeId);
}

// Loads the manager/admin viewer row to decide access and the default company scope.
export function fetchManagerViewerByAuthUserId(authUserId: string) {
  return supabase
    .from("employees")
    .select("id,company_id,company_role")
    .eq("auth_user_id", authUserId)
    .maybeSingle();
}

// Lists companies for admin selectors.
export function fetchCompanies() {
  return supabase
    .from("companies")
    .select("id,name,hidden_from_registration")
    .order("name", { ascending: true });
}

// Sets whether a company is hidden from self-registration search
// suggestions and can no longer be matched/created there by name (e.g. a
// confidential pilot customer). Admin-only, from the companies management view.
export function updateCompanyHiddenFromRegistration(companyId: string, hidden: boolean) {
  return supabase.from("companies").update({ hidden_from_registration: hidden }).eq("id", companyId);
}

// Loads the company display name for profile/readiness panels.
export function fetchCompanyById(companyId: string) {
  return supabase.from("companies").select("id,name").eq("id", companyId).maybeSingle();
}

// Ranked "did you mean" suggestions for a partially-typed company name,
// matched on canonical names and known aliases (e.g. "TK" -> takeKONTROL).
// Advisory only -- the registration UI decides whether to accept one.
export function searchCompaniesFuzzy(query: string, limit = 5) {
  return supabase.rpc("search_companies_fuzzy", { p_query: query, p_limit: limit });
}

// Resolves a free-typed company name to an existing company (exact or
// normalized-exact match) or creates a new one. Never silently merges a
// merely similar-but-different name -- that choice is made in the UI first.
// find_or_create_company always returns exactly one row.
export async function findOrCreateCompany(name: string) {
  const { data, error } = await supabase.rpc("find_or_create_company", { p_name: name });
  return { data: (data?.[0] ?? null) as CompanyResolution | null, error };
}

// Records a user-confirmed alias (e.g. they typed "TK" and picked
// "takeKONTROL" from the suggestion list) so the same text resolves
// instantly next time.
export function addCompanyAlias(companyId: string, alias: string) {
  return supabase.rpc("add_company_alias", { p_company_id: companyId, p_alias: alias });
}

// Creates a new company from the admin companies management view.
export function createCompany(name: string) {
  return supabase.from("companies").insert({ name }).select("id,name").single();
}

// Renames a company from the admin companies management view.
export function updateCompanyName(companyId: string, name: string) {
  return supabase.from("companies").update({ name }).eq("id", companyId);
}

// Deletes a company from the admin companies management view. The UI only allows this
// once the company has no employees left, since employees cascade-delete with it.
export function deleteCompany(companyId: string) {
  return supabase.from("companies").delete().eq("id", companyId);
}

// Loads a single company name when only the readiness-check label is needed.
export function fetchCompanyNameById(companyId: string) {
  return supabase.from("companies").select("name").eq("id", companyId).maybeSingle();
}

// Loads the manager-maintained emergency contact for a company.
export function fetchCompanyEmergencyContact(companyId: string) {
  return supabase
    .from("company_emergency_contacts")
    .select("id,company_id,name,email,phone_number,created_at,updated_at")
    .eq("company_id", companyId)
    .maybeSingle<CompanyEmergencyContact>();
}

// Creates or updates the single manager-maintained emergency contact for a company.
export function upsertCompanyEmergencyContact(
  companyId: string,
  values: { name: string; email: string; phone_number: string | null }
) {
  return supabase
    .from("company_emergency_contacts")
    .upsert(
      {
        company_id: companyId,
        name: values.name,
        email: values.email,
        phone_number: values.phone_number,
        updated_at: new Date().toISOString(),
      },
      { onConflict: "company_id" }
    )
    .select("id,company_id,name,email,phone_number,created_at,updated_at")
    .single<CompanyEmergencyContact>();
}

// Removes the manager-maintained emergency contact for a company.
export function deleteCompanyEmergencyContact(companyId: string) {
  return supabase.from("company_emergency_contacts").delete().eq("company_id", companyId);
}

// Lists events for the employee occasion picker and admin selectors.
export function fetchEvents() {
  return supabase
    .from("events")
    .select("id,name,event_date,occasion_keyword,created_at,updated_at")
    .order("event_date", { ascending: false, nullsFirst: false });
}

// Creates a new event from the admin events management view.
export function createEvent(name: string, eventDate: string | null, occasionKeyword: string) {
  return supabase
    .from("events")
    .insert({ name, event_date: eventDate, occasion_keyword: occasionKeyword })
    .select("id,name,event_date,occasion_keyword,created_at,updated_at")
    .single();
}

// Renames/reschedules an event from the admin events management view. The keyword is
// recomputed and overwritten in place -- nothing else references it (question rows and
// employee_assessment_occasions link by event_id), so no cascade is needed.
export function updateEvent(
  eventId: string,
  values: { name: string; event_date: string | null; occasion_keyword: string }
) {
  return supabase
    .from("events")
    .update({ ...values, updated_at: new Date().toISOString() })
    .eq("id", eventId);
}

// Deletes an event from the admin events management view. The UI only allows this once
// the event has no participants left.
export function deleteEvent(eventId: string) {
  return supabase.from("events").delete().eq("id", eventId);
}

// Lists the participants (with name/email) of an event for the admin events management view.
export function fetchEventParticipants(eventId: string) {
  return supabase
    .from("employee_assessment_occasions")
    .select("employee_id,updated_at,employees(id,first_name,last_name,company_email)")
    .eq("event_id", eventId);
}

// Looks up an employee by their company email, so an admin can add them to an event by email.
export function fetchEmployeeByEmail(email: string) {
  return supabase
    .from("employees")
    .select("id,first_name,last_name,company_email")
    .eq("company_email_norm", email.trim().toLowerCase())
    .maybeSingle();
}

// Lists every registered employee's name/email, for the admin events participant-picker autocomplete.
export function fetchEmployeeDirectory() {
  return supabase
    .from("employees")
    .select("id,first_name,last_name,company_email")
    .order("last_name", { ascending: true });
}

// Removes an employee from an event's participant list (admin-only).
export function removeEventParticipant(eventId: string, employeeId: string) {
  return supabase
    .from("employee_assessment_occasions")
    .delete()
    .eq("event_id", eventId)
    .eq("employee_id", employeeId);
}

// Reads company ids across employees so the admin dashboard can show employee counts by company.
export function fetchEmployeeCompanyIds() {
  return supabase.from("employees").select("company_id");
}

// Reads each employee's company and free-text department so the admin companies view can
// show employee counts and the distinct department names already in use per company.
export function fetchEmployeeCompanyAndDepartment() {
  return supabase.from("employees").select("company_id,department");
}

// Renames a department string across every employee in a company that currently has it, so
// the admin companies view can edit a department name in place instead of just displaying it.
export function renameCompanyDepartment(companyId: string, oldName: string, newName: string) {
  return supabase
    .from("employees")
    .update({ department: newName })
    .eq("company_id", companyId)
    .eq("department", oldName);
}

// Lists employees in a company for manager dashboard aggregation.
export function fetchEmployeesByCompanyId(companyId: string) {
  return supabase.from("employees").select("id,department").eq("company_id", companyId);
}

// Lists the employees who selected a given event (id + department), for the
// admin/manager event dashboard -- including its department breakdown tab.
export async function fetchEmployeesByOccasion(eventId: string) {
  const { data, error } = await supabase
    .from("employee_assessment_occasions")
    .select("employee_id,employees(department)")
    .eq("event_id", eventId);

  return {
    data: (data ?? []).map((row: any) => ({
      id: row.employee_id as string,
      department: (row.employees?.department as string | null) ?? null,
    })),
    error,
  };
}

// Bulk-reads participant counts per event for the admin events list.
export async function fetchEventParticipantCounts() {
  const { data, error } = await supabase.from("employee_assessment_occasions").select("event_id");

  return {
    data: (data ?? []).reduce((counts, row) => {
      const eventId = row.event_id as string | null;
      if (eventId) counts[eventId] = (counts[eventId] ?? 0) + 1;
      return counts;
    }, {} as Record<string, number>),
    error,
  };
}

// Lists every registered user across all companies for the admin user-rights management view.
export function fetchAllEmployeesForAdmin() {
  return supabase
    .from("employees")
    .select("id,auth_user_id,first_name,last_name,company_email,company_id,department,company_role,companies(name)")
    .order("last_name", { ascending: true });
}

// Updates an employee's company role (employee/manager/admin) from the admin user-rights management view.
export function updateEmployeeCompanyRole(employeeId: string, role: CompanyRole) {
  return supabase.from("employees").update({ company_role: role }).eq("id", employeeId);
}

// Updates an employee's company assignment and/or department from the admin user-rights management view.
export function updateEmployeeAdminFields(
  employeeId: string,
  values: { company_id?: string; department?: string }
) {
  return supabase.from("employees").update(values).eq("id", employeeId);
}

// Permanently deletes another user's account through the admin-delete-employee edge function.
export function deleteEmployeeAsAdmin(employeeId: string, token: string) {
  return supabase.functions.invoke<{ ok: true } | { error: string }>("admin-delete-employee", {
    body: { employee_id: employeeId },
    headers: { Authorization: `Bearer ${token}` },
  });
}

// Lists employee ids in a company for company-average score calculations.
export function fetchCompanyEmployeeIds(companyId: string) {
  return supabase.from("employees").select("id").eq("company_id", companyId);
}

// Loads the full profile row displayed and edited on the profile page.
export function fetchProfileEmployeeByAuthUserId(authUserId: string) {
  return supabase
    .from("employees")
    .select(
      [
        "id",
        "company_id",
        "auth_user_id",
        "first_name",
        "last_name",
        "position",
        "department",
        "company_email",
        "company_address",
        "personal_address",
        "location",
        "company_role",
        "availability_submitted_at",
        "avatar_url",
        "is_anonymous",
        "anonymity_updated_at",
      ].join(",")
    )
    .eq("auth_user_id", authUserId)
    .maybeSingle<EmployeeRow>();
}

// Loads the single legacy employee_scores row used by EmployeeScreen.
export function fetchEmployeeScoreByEmployeeId(employeeId: string) {
  return supabase.from("employee_scores").select("kontrol_score").eq("employee_id", employeeId).maybeSingle();
}

// Loads the current employee's three KONTROL score rows.
export function fetchKontrolScoresByEmployeeId(employeeId: string) {
  return supabase
    .from("kontrol_scores")
    .select("score_type,kontrol_score,updated_at")
    .eq("employee_id", employeeId)
    .in("score_type", SCORE_TYPES as unknown as string[]);
}

// Loads score rows for many employees, used by company averages and manager dashboard summaries.
export function fetchKontrolScoresByEmployeeIds(employeeIds: string[]) {
  return supabase
    .from("kontrol_scores")
    .select("employee_id,score_type,kontrol_score")
    .in("employee_id", employeeIds)
    .in("score_type", SCORE_TYPES as unknown as string[]);
}

// Snapshots every current kontrol_scores row for this employee into
// event_kontrol_scores for their currently selected event. Call once when
// the employee presses Finish at the end of the assessment flow, regardless
// of whether any individual answer changed, so the event dashboard always
// ends up with all three scores instead of only whichever ones happened to
// be (re)answered after the event was linked.
export function finalizeEventKontrolScores(employeeId: string) {
  return supabase.rpc("finalize_event_kontrol_scores", { p_employee_id: employeeId });
}

// Recomputes the availability index (AVI) and its event_availability_index
// snapshot for the employee's currently selected event. Same idea as
// finalizeEventKontrolScores, but AVI already has its own recompute RPC
// (also fired by triggers on av_assessment_answers) -- call it directly so
// Finish always leaves a snapshot for this event too, even if nothing
// changed this visit.
export function recalculateEmployeeAvi(employeeId: string) {
  return supabase.rpc("recalculate_employee_avi", { p_employee_id: employeeId });
}

// Loads the historical per-event score snapshots for the admin/manager events dashboard --
// frozen at the moment each employee's score was (re)computed while this event was their
// selected occasion, so a later event doesn't erase these numbers.
export function fetchEventKontrolScores(eventId: string, employeeIds: string[]) {
  return supabase
    .from("event_kontrol_scores")
    .select("employee_id,score_type,kontrol_score")
    .eq("event_id", eventId)
    .in("employee_id", employeeIds)
    .in("score_type", SCORE_TYPES as unknown as string[]);
}

// Checks whether the required baseline score types exist for post-login routing.
export function fetchCompletedScoreTypesForEmployee(employeeId: string) {
  return supabase
    .from("kontrol_scores")
    .select("score_type")
    .eq("employee_id", employeeId)
    .in("score_type", SCORE_TYPES as unknown as string[]);
}

// Finds the newest assessment timestamp for the profile page.
export function fetchLatestAssessmentTimestamp(employeeId: string) {
  return supabase
    .from("kontrol_scores")
    .select("updated_at")
    .eq("employee_id", employeeId)
    .in("score_type", SCORE_TYPES as unknown as string[])
    .order("updated_at", { ascending: false })
    .limit(1)
    .maybeSingle();
}

// Loads the current user's saved post-score feedback, if they already submitted it.
export function fetchKontrolScoreFeedbackByEmployeeId(employeeId: string) {
  return supabase
    .from("kontrol_score_feedback")
    .select("employee_id,answers,submitted_at,updated_at")
    .eq("employee_id", employeeId)
    .maybeSingle<KontrolScoreFeedbackRow>();
}

// Loads active post-score feedback questions and answer options.
export function fetchKontrolScoreFeedbackQuestions() {
  return supabase
    .from("kontrol_score_feedback_questions")
    .select(
      "id,question_code,section_key,input_type,question_en,question_de,answer_options,is_required,order_index,feedback_audience,trigger_key"
    )
    .eq("is_active", true)
    .order("order_index", { ascending: true })
    .returns<KontrolScoreFeedbackQuestion[]>();
}

// Loads every employee's submitted KONTROL score feedback for the admin raw-responses view.
export function fetchAllKontrolScoreFeedbackForAdmin() {
  return supabase
    .from("kontrol_score_feedback")
    .select(
      "employee_id,answers,submitted_at,updated_at,employees(first_name,last_name,company_email,company_id,companies(name))"
    )
    .order("submitted_at", { ascending: false })
    .returns<KontrolScoreFeedbackAdminRow[]>();
}

// Loads submitted KONTROL score feedback for a set of employees, for the manager
// dashboard's aggregated team-feedback tab.
export function fetchKontrolScoreFeedbackByEmployeeIds(employeeIds: string[]) {
  return supabase
    .from("kontrol_score_feedback")
    .select("employee_id,answers")
    .in("employee_id", employeeIds)
    .returns<Pick<KontrolScoreFeedbackRow, "employee_id" | "answers">[]>();
}

// Saves or updates one post-score feedback response for the current user.
export function upsertKontrolScoreFeedback(
  employeeId: string,
  answers: KontrolScoreFeedbackAnswers
) {
  return supabase
    .from("kontrol_score_feedback")
    .upsert(
      {
        employee_id: employeeId,
        answers,
        submitted_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
      },
      { onConflict: "employee_id" }
    )
    .select("employee_id,answers,submitted_at,updated_at")
    .single<KontrolScoreFeedbackRow>();
}

// Lists active modules for simple module pickers.
export function fetchActiveModules() {
  return supabase
    .from("modules")
    .select("*")
    .eq("is_active", true)
    .order("order_index", { ascending: true, nullsFirst: false })
    .order("created_at", { ascending: true });
}

// Loads one module by id for module detail navigation.
export function fetchModuleById(id: string) {
  return supabase.from("modules").select("*").eq("id", id).single();
}

// Lists active modules with nested task categories and tasks for the action plan overview.
export function fetchActiveModulesWithTasks() {
  return supabase
    .from("modules")
    .select(MODULE_WITH_TASKS_SELECT)
    .eq("is_active", true)
    .order("order_index", { ascending: true, nullsFirst: false })
    .order("created_at", { ascending: true });
}

// Loads one active module by order with nested task categories and tasks for module pages.
export function fetchActiveModuleWithTasksByOrder(orderIndex: number) {
  return supabase
    .from("modules")
    .select(MODULE_WITH_TASKS_SELECT)
    .eq("is_active", true)
    .eq("order_index", orderIndex)
    .single();
}

// Loads saved task completion state for an employee's action plan.
export function fetchModuleTaskProgress(employeeId: string) {
  return supabase.from("employee_module_task_progress").select("module_task_id,is_done").eq("employee_id", employeeId);
}

// Saves a completed module task for an employee, creating the row if needed.
export function upsertModuleTaskDone(employeeId: string, moduleTaskId: string) {
  return supabase
    .from("employee_module_task_progress")
    .upsert(
      {
        employee_id: employeeId,
        module_task_id: moduleTaskId,
        is_done: true,
        updated_at: new Date().toISOString(),
      },
      { onConflict: "employee_id,module_task_id" }
    );
}

// Removes a module task progress row when an employee marks a task as not done.
export function deleteModuleTaskProgress(employeeId: string, moduleTaskId: string) {
  return supabase
    .from("employee_module_task_progress")
    .delete()
    .eq("employee_id", employeeId)
    .eq("module_task_id", moduleTaskId);
}

// Loads personal module recommendations for the KONTROL score page.
export function fetchRecommendedModulesByEmployeeId(employeeId: string) {
  return supabase
    .from("employee_module_recommendations")
    .select("module_id,module_order,trigger_reasons,modules(name_en,name_de)")
    .eq("employee_id", employeeId)
    .eq("is_recommended", true)
    .order("module_order", { ascending: true });
}

// Loads rich module recommendations for manager dashboard impact analysis.
export function fetchManagerModuleRecommendations(employeeIds: string[]) {
  return supabase
    .from("employee_module_recommendations")
    .select(
      "employee_id,module_id,module_order,trigger_reasons,modules(name_en,name_de,description_en,description_de,kontrol_score_pct,estimated_minutes,module_task_categories(order_index,title_en,title_de,module_tasks(task_en,task_de)))"
    )
    .in("employee_id", employeeIds)
    .eq("is_recommended", true)
    .order("module_order", { ascending: true });
}

// Checks whether an employee has completed all tasks for a recommended module.
export function isEmployeeModuleCompleted(employeeId: string, moduleId: string) {
  return supabase.rpc("is_employee_module_completed", {
    p_employee_id: employeeId,
    p_module_id: moduleId,
  });
}

// Loads active per-module feedback questions shown after a module is completed.
export function fetchEmployeeModuleFeedbackQuestions() {
  return supabase
    .from("employee_module_feedback_questions")
    .select("id,question_code,input_type,question_en,question_de,answer_options,is_required,order_index")
    .eq("is_active", true)
    .order("is_required", { ascending: false })
    .order("order_index", { ascending: true })
    .returns<EmployeeModuleFeedbackQuestion[]>();
}

// Checks if the employee already submitted feedback for this completed module.
export function fetchEmployeeModuleFeedback(employeeId: string, moduleId: string) {
  return supabase
    .from("employee_module_feedback")
    .select("employee_id,module_id,module_order,usefulness_rating,helpfulness_note,submitted_at,updated_at")
    .eq("employee_id", employeeId)
    .eq("module_id", moduleId)
    .maybeSingle<EmployeeModuleFeedbackRow>();
}

// Saves one feedback response per employee/module completion.
export function upsertEmployeeModuleFeedback(params: {
  employeeId: string;
  moduleId: string;
  moduleOrder: number;
  usefulnessRating: number;
  helpfulnessNote: string;
}) {
  const now = new Date().toISOString();

  return supabase
    .from("employee_module_feedback")
    .upsert(
      {
        employee_id: params.employeeId,
        module_id: params.moduleId,
        module_order: params.moduleOrder,
        usefulness_rating: params.usefulnessRating,
        helpfulness_note: params.helpfulnessNote.trim() || null,
        submitted_at: now,
        updated_at: now,
      },
      { onConflict: "employee_id,module_id" }
    )
    .select("employee_id,module_id,module_order,usefulness_rating,helpfulness_note,submitted_at,updated_at")
    .single<EmployeeModuleFeedbackRow>();
}

// Loads personal EKS action recommendations for the KONTROL score page.
export function fetchEksRecommendationsByEmployeeId(employeeId: string) {
  return supabase
    .from("employee_eks_recommendations")
    .select("eks_module_code,question_code,trigger_answer,is_priority,is_recommended")
    .eq("employee_id", employeeId)
    .eq("is_recommended", true)
    .order("is_priority", { ascending: false })
    .order("eks_module_code", { ascending: true });
}

// Loads all PBG assessment questions.
export function fetchPbgQuestions() {
  return supabase
    .from("pbg_questions")
    .select("id,question_code,question_en,question_de,question_weight,answer_options,pbg_type,created_at,event_id")
    .then(({ data, error }) => ({
      data: data?.filter((question) => questionMatchesAssessmentOccasion(question)) ?? data,
      error,
    }));
}

// Loads all ExtPRS assessment questions in creation order.
export function fetchExtPrsQuestions() {
  return supabase
    .from("extprs_questions")
    .select("id,question_en,question_de,answer_options,created_at,question_code,event_id")
    .order("created_at", { ascending: true })
    .then(({ data, error }) => ({
      data: data?.filter((question) => questionMatchesAssessmentOccasion(question)) ?? data,
      error,
    }));
}

// Loads all EKS assessment questions in creation order.
export function fetchEksQuestions() {
  return supabase
    .from("eks_questions")
    .select("id,question_code,question_en,question_de,question_weight,answer_options,created_at,event_id")
    .order("created_at", { ascending: true })
    .then(({ data, error }) => ({
      data: data?.filter((question) => questionMatchesAssessmentOccasion(question)) ?? data,
      error,
    }));
}

// Loads saved PBG answers for an employee.
export function fetchPbgAnswers(employeeId: string) {
  return supabase.from("pbg_answers").select("pbg_question_id,given_answer").eq("employee_id", employeeId);
}

// Loads saved ExtPRS answers for an employee.
export function fetchExtPrsAnswers(employeeId: string) {
  return supabase.from("extprs_answers").select("extprs_question_id,given_answer").eq("employee_id", employeeId);
}

// Loads saved EKS answers for an employee.
export function fetchEksAnswers(employeeId: string) {
  return supabase.from("eks_answers").select("eks_question_id,given_answer").eq("employee_id", employeeId);
}

// Loads the availability questions that feed AVI for the employee's currently selected event.
export function fetchAvAssessmentQuestions() {
  return supabase
    .from("av_assessment_questions")
    .select("id,question_code,short_name,question_en,question_de,severity_weight,feeds,answer_options,is_scored,flag_code,event_id")
    .order("question_code", { ascending: true })
    .returns<AvAssessmentQuestion[]>()
    .then(({ data, error }) => ({
      data: data?.filter((question) => questionMatchesAssessmentOccasion(question)) ?? data,
      error,
    }));
}

// Loads saved AV answers for an employee.
export function fetchAvAssessmentAnswers(employeeId: string) {
  return supabase
    .from("av_assessment_answers")
    .select("question_id,given_answer")
    .eq("employee_id", employeeId);
}

// Loads the separately stored AVI result. This is intentionally outside kontrol_scores.
export function fetchAvailabilityIndexResult(employeeId: string) {
  return supabase
    .from("employee_availability_index")
    .select("employee_id,avi_raw,avi_band,av_answers,av_skipped,flags,updated_at")
    .eq("employee_id", employeeId)
    .maybeSingle();
}

// Saves or updates one PBG answer.
export function upsertPbgAnswer(employeeId: string, questionId: string, givenAnswer: string) {
  return supabase
    .from("pbg_answers")
    .upsert({ employee_id: employeeId, pbg_question_id: questionId, given_answer: givenAnswer }, { onConflict: "employee_id,pbg_question_id" });
}

// Saves or updates one ExtPRS answer.
export function upsertExtPrsAnswer(employeeId: string, questionId: string, givenAnswer: string) {
  return supabase
    .from("extprs_answers")
    .upsert({ employee_id: employeeId, extprs_question_id: questionId, given_answer: givenAnswer }, { onConflict: "employee_id,extprs_question_id" });
}

// Saves or updates one EKS answer.
export function upsertEksAnswer(employeeId: string, questionId: string, givenAnswer: string) {
  return supabase
    .from("eks_answers")
    .upsert({ employee_id: employeeId, eks_question_id: questionId, given_answer: givenAnswer }, { onConflict: "employee_id,eks_question_id" });
}

// Saves one AV answer and lets database triggers recalculate AVI.
export function upsertAvAssessmentAnswer(employeeId: string, questionId: string, givenAnswer: string) {
  return supabase
    .from("av_assessment_answers")
    .upsert(
      { employee_id: employeeId, question_id: questionId, given_answer: givenAnswer },
      { onConflict: "employee_id,question_id" }
    );
}

// Marks one AV question skipped. Skipped scored AV questions contribute zero to AVI.
export function markAvAssessmentQuestionSkipped(employeeId: string, questionCode: string) {
  return supabase.rpc("mark_av_assessment_question_skipped", {
    p_employee_id: employeeId,
    p_question_code: questionCode,
  });
}

// Removes one AV skip marker when the question is answered.
export function unmarkAvAssessmentQuestionSkipped(employeeId: string, questionCode: string) {
  return supabase.rpc("unmark_av_assessment_question_skipped", {
    p_employee_id: employeeId,
    p_question_code: questionCode,
  });
}

// Loads skipped assessment question codes for the progress sidebar and skipped badges.
export function fetchAssessmentAudit(employeeId: string) {
  return supabase
    .from("employee_assessment_audit")
    .select("skipped_questions")
    .eq("employee_id", employeeId)
    .maybeSingle();
}

// Marks one assessment question as skipped via the audit RPC.
export function markAssessmentQuestionSkipped(employeeId: string, questionCode: string) {
  return supabase.rpc("mark_assessment_question_skipped", {
    p_employee_id: employeeId,
    p_question_code: questionCode,
  });
}

// Removes one assessment question from the skipped set after the user answers it.
export function unmarkAssessmentQuestionSkipped(employeeId: string, questionCode: string) {
  return supabase.rpc("unmark_assessment_question_skipped", {
    p_employee_id: employeeId,
    p_question_code: questionCode,
  });
}

// Loads profile-assessment records from the four normalized profile tables.
export function fetchProfileAssessmentRows(employeeId: string) {
  return Promise.all([
    supabase.from("employee_identity_household").select("*").eq("employee_id", employeeId).maybeSingle(),
    supabase.from("employee_health_mobility").select("*").eq("employee_id", employeeId).maybeSingle(),
    supabase.from("employee_social").select("*").eq("employee_id", employeeId).maybeSingle(),
    supabase.from("employee_practical").select("*").eq("employee_id", employeeId).maybeSingle(),
  ]);
}

// Saves profile-assessment answers into the four normalized profile tables.
export function upsertProfileAssessmentRows(payloads: {
  identityPayload: Record<string, unknown>;
  healthPayload: Record<string, unknown>;
  socialPayload: Record<string, unknown>;
  practicalPayload: Record<string, unknown>;
}) {
  return Promise.all([
    supabase.from("employee_identity_household").upsert(payloads.identityPayload, { onConflict: "employee_id" }),
    supabase.from("employee_health_mobility").upsert(payloads.healthPayload, { onConflict: "employee_id" }),
    supabase.from("employee_social").upsert(payloads.socialPayload, { onConflict: "employee_id" }),
    supabase.from("employee_practical").upsert(payloads.practicalPayload, { onConflict: "employee_id" }),
  ]);
}

// Refreshes derived assessment audit, flag and module recommendation rows after profile changes.
export function recalculateProfileAssessmentDerivedRows(employeeId: string) {
  return Promise.allSettled([
    supabase.rpc("recalculate_employee_assessment_audit", { p_employee_id: employeeId }),
    supabase.rpc("recalculate_employee_assessment_flags", { p_employee_id: employeeId }),
    supabase.rpc("recalculate_employee_module_recommendations", { p_employee_id: employeeId }),
  ]);
}

// Loads assessment flags for manager dashboard risk summaries.
export function fetchAssessmentFlags(employeeIds: string[]) {
  return supabase
    .from("employee_assessment_flags")
    .select("employee_id,spof,medical,spof_reasons,medical_reasons")
    .in("employee_id", employeeIds);
}

// Loads the historical per-event flag snapshots for the admin/manager events dashboard.
export function fetchEventAssessmentFlags(eventId: string, employeeIds: string[]) {
  return supabase
    .from("event_assessment_flags")
    .select("employee_id,spof,medical,spof_reasons,medical_reasons")
    .eq("event_id", eventId)
    .in("employee_id", employeeIds);
}

// Loads the current employee's own SPoF and medical assessment flags.
export function fetchAssessmentFlagsByEmployeeId(employeeId: string) {
  return supabase
    .from("employee_assessment_flags")
    .select("spof,medical,spof_reasons,medical_reasons")
    .eq("employee_id", employeeId)
    .maybeSingle();
}

// Reads the care-dependent count backing the employee's own care flag.
export function fetchEmployeeCareDependents(employeeId: string) {
  return supabase
    .from("employee_social")
    .select("people_needing_care_count")
    .eq("employee_id", employeeId)
    .maybeSingle();
}

// Loads manager-only availability view rows for availability summaries.
export function fetchManagerAvailabilityRows(employeeIds: string[]) {
  return supabase.from("manager_employee_availability_view").select("*").in("employee_id", employeeIds);
}

// Loads question/answer data needed to estimate module issue-resolution impact on the manager dashboard.
export function fetchManagerImpactInputs(employeeIds: string[]) {
  return Promise.all([
    supabase.from("pbg_questions").select("id,question_code,question_weight,pbg_type,answer_options"),
    supabase.from("extprs_questions").select("id,question_code,is_scored,answer_options"),
    supabase.from("pbg_answers").select("employee_id,pbg_question_id,given_answer").in("employee_id", employeeIds),
    supabase.from("extprs_answers").select("employee_id,extprs_question_id,given_answer").in("employee_id", employeeIds),
    supabase.from("employee_answer_improvements").select("employee_id,question_code,improved_answer").in("employee_id", employeeIds),
    supabase.from("module_task_categories").select("module_id,module_tasks(id)"),
    supabase.from("employee_module_task_progress").select("employee_id,module_task_id,is_done").in("employee_id", employeeIds),
  ]);
}

// Loads public takeKONTROL emergency contacts before local filtering removes placeholder rows.
export function fetchEmergencyContacts() {
  return supabase.from("emergency_contacts").select("*").order("last_name", { ascending: true });
}

// Creates a signed URL for a Supabase Storage object, used for videos and avatars.
export function createSignedStorageUrl(bucket: string, path: string, expiresInSec: number) {
  return supabase.storage.from(bucket).createSignedUrl(path, expiresInSec);
}

// Calls request-invite to decide whether an email should log in or set an initial password.
export function requestInvite(email: string) {
  return supabase.functions.invoke<RequestInviteResponse>("request-invite", {
    body: { email },
  });
}

// Checks whether a registration email's domain can plausibly receive mail
// (MX/A record present), before creating an auth account for it. Runs
// server-side since this app's CSP blocks the browser from calling an
// external DNS-over-HTTPS provider directly.
export function checkEmailDomain(email: string) {
  return supabase.functions.invoke<{ ok: boolean }>("check-email-domain", {
    body: { email },
  });
}

// Calls link-employee to connect an authenticated user to a pre-provisioned employee row.
export function linkEmployee(email: string, token: string) {
  return supabase.functions.invoke<LinkEmployeeResponse>("link-employee", {
    body: { email },
    headers: { Authorization: `Bearer ${token}` },
  });
}

// Calls self-register to create the employee row for public self-registration.
export function selfRegister(params: {
  email: string;
  authUserId: string;
  companyId: string;
  department: string;
  firstName: string;
  lastName: string;
  position: string;
  personalAddress: string;
  anonymous: boolean;
}) {
  return supabase.functions.invoke<SelfRegisterResponse>("self-register", {
    body: {
      email: params.email,
      auth_user_id: params.authUserId,
      company_id: params.companyId,
      department: params.department,
      first_name: params.firstName,
      last_name: params.lastName,
      position: params.position,
      personal_address: params.personalAddress,
      anonymous: params.anonymous,
    },
  });
}

// Sends a password reset email through the password-reset edge function.
export function sendPasswordResetEmail(email: string, origin: string) {
  return supabase.functions.invoke("send-password-reset", {
    body: { email, origin },
  });
}

// Confirms email tokens through the confirm-email edge function.
export function confirmEmail(token: string) {
  return supabase.functions.invoke("confirm-email", {
    body: { token },
  });
}

// Resets a password through the reset-password edge function.
export function resetPassword(token: string, newPassword: string) {
  return supabase.functions.invoke("reset-password", {
    body: { token, new_password: newPassword },
  });
}

// Uploads or replaces the current user's avatar through the set-avatar edge function.
export function uploadAvatar(file: File, token: string) {
  const formData = new FormData();
  formData.append("file", file);

  return supabase.functions.invoke("set-avatar", {
    body: formData,
    headers: { Authorization: `Bearer ${token}` },
  });
}

// Removes the current user's avatar through the set-avatar edge function.
export function removeAvatar(token: string) {
  return supabase.functions.invoke("set-avatar", {
    method: "DELETE",
    headers: { Authorization: `Bearer ${token}` },
  });
}

// Enables/disables profile anonymity through an RPC that owns the privacy-sensitive update.
export function setMyAnonymity(isAnonymous: boolean) {
  return supabase.rpc("set_my_anonymity", {
    p_is_anonymous: isAnonymous,
  });
}

// Saves non-anonymous profile details through the profile details RPC.
export function updateMyProfileDetails(values: {
  firstName: string;
  lastName: string;
  position: string;
  department: string;
  personalAddress: string;
}) {
  return supabase.rpc("update_my_profile_details", {
    p_first_name: values.firstName,
    p_last_name: values.lastName,
    p_position: values.position,
    p_department: values.department,
    p_personal_address: values.personalAddress,
  });
}

// Sends a user's profile support question to the takeKONTROL team.
export function sendProfileQuestion(employeeId: string, message: string) {
  return supabase.functions.invoke("send-profile-question", {
    body: { employee_id: employeeId, message },
  });
}

// Performs public database cleanup for the current account before auth-user deletion.
export function deleteMyAccountData() {
  return supabase.rpc("delete_my_account");
}

// Calls the export-user-data edge function with fetch because the result is a downloadable blob.
export function exportUserData(token: string) {
  return fetch(`${process.env.REACT_APP_SUPABASE_URL}/functions/v1/export-user-data`, {
    method: "GET",
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });
}

// Calls the delete-account edge function to remove the Supabase Auth user after public data cleanup.
export function deleteAuthAccount(token: string) {
  return fetch(`${process.env.REACT_APP_SUPABASE_URL}/functions/v1/delete-account`, {
    method: "POST",
    headers: {
      Authorization: `Bearer ${token}`,
      apikey: process.env.REACT_APP_SUPABASE_ANON_KEY!,
      "Content-Type": "application/json",
    },
  });
}
